/**
 * ProShip Direct v2 — product master: the sheet contract.
 *
 * MIRRORED from the admin module at proship-backend/src/app/pages/merchants/ProductMaster.js, which
 * is the same collection seen from the other side. The header row is the one the real sample file
 * carries (GET order/Download_samples?fileName=PRODUCT_MASTER_SAMPLE), in that exact order — the
 * upsert reads columns positionally as well as by name, so the order is contract, not preference.
 *
 * THE WRITE PATH IS THE SHEET, and that is not a design choice we get to make. product_master rows
 * come back from generic/search with `_id` as {timestamp, date} and `id: null` — no hex ObjectId —
 * so generic/update rejects them ("Nothing to update - Id is missing"), and there is no
 * product/update endpoint. The only way to change a product is to upsert a one-row sheet keyed on
 * merchantId + skuCode. That is why editing one image means resending every column the product
 * already has: anything left out of the sheet is blanked by the upsert.
 *
 * Unlike the rest of this module, nothing here is a fixture. The page reads and writes live data.
 */

/**
 * The sample sheet's header row, in order. Do not reorder.
 *
 * TAX IS AN EXTENSION, and deliberately last. product_master has no tax column today — the sample
 * file the backend serves ends at image10, and order creation proves the gap: it reads name, mrp
 * and hsnCode off the SKU and then hard-codes the line's tax to 0, because there is nothing to read.
 *
 * Appending rather than inserting keeps every existing column at the index a positional reader
 * expects, so the worst case is an unknown trailing column rather than a shifted sheet.
 *
 * TODO(ravi, 2026-09): confirm product/uploadSkuDetails tolerates the extra column, and add
 * taxPercent to the PRODUCT_MASTER_SAMPLE file. Until then the value is captured and sent but will
 * not persist, and order creation keeps defaulting a line’s tax to 0.
 */
export const PM_SHEET_HEADERS = [
  'skuCode', 'productId', 'name', 'brand', 'category', 'subCategory', 'mrp', 'eanCode', 'upcCode',
  'hsnCode', 'uom', 'weight', 'length', 'breath', 'height', 'productStatus', 'imageUploaded',
  'image1', 'image2', 'image3', 'image4', 'image5', 'image6', 'image7', 'image8', 'image9', 'image10',
  'taxPercent',
];

/** Most images one product can carry, set by the sheet's image1..image10 columns. */
export const MAX_PRODUCT_IMAGES = 10;

const csvCell = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * A one-row sheet for a single product, carrying its existing columns plus the given images.
 *
 * Every column the product already has is resent deliberately — the upsert replaces the row, so a
 * column omitted here is a column wiped there. Editing an image must not silently clear an HSN code.
 */
export function buildProductMasterCsv(product, images) {
  const imgs = (images || []).filter(Boolean).slice(0, MAX_PRODUCT_IMAGES);
  const imageCols = Array.from({ length: MAX_PRODUCT_IMAGES }, (unused, i) => imgs[i] || '');

  const row = [
    product.skuCode, product.productId, product.name, product.brand, product.category,
    product.subCategory, product.mrp, product.eanCode, product.upcCode, product.hsnCode, product.uom,
    product.weight, product.length, product.breath, product.height, product.productStatus,
    imgs.length ? 'Yes' : 'No', ...imageCols,
    product.taxPercent,
  ];

  return `${PM_SHEET_HEADERS.join(',')}\n${row.map(csvCell).join(',')}\n`;
}

/**
 * The sheet's columns, grouped for the manual form.
 *
 * Same field names and same order as PM_SHEET_HEADERS, because the form's output goes through
 * buildProductMasterCsv into the same upsert the bulk sheet uses — one write path, two ways in.
 *
 * Only skuCode is genuinely required: it is the upsert key, and a row without one cannot be matched
 * to anything. Everything else can arrive later from the channel when an order for the SKU is
 * received, which is why the form asks rather than insists.
 */
export const PRODUCT_FIELD_GROUPS = [
  {
    key: 'identity',
    title: 'Identity',
    fields: [
      { key: 'skuCode', label: 'SKU code', required: true, mono: true, help: 'The key everything matches on' },
      { key: 'name', label: 'Product name', width: 8 },
      { key: 'productId', label: 'Product ID', help: 'Your own internal id, if you use one' },
    ],
  },
  {
    key: 'classification',
    title: 'Brand and category',
    fields: [
      { key: 'brand', label: 'Brand' },
      { key: 'category', label: 'Category', options: 'categories', help: 'ProShip’s category list' },
      { key: 'subCategory', label: 'Sub category', options: 'subCategories', help: 'Yours — type a new one to add it' },
    ],
  },
  {
    key: 'measurements',
    title: 'Weight and dimensions',
    fields: [
      { key: 'weight', label: 'Weight', unit: 'g', type: 'number' },
      { key: 'length', label: 'Length', unit: 'cm', type: 'number' },
      { key: 'breath', label: 'Breadth', unit: 'cm', type: 'number' },
      { key: 'height', label: 'Height', unit: 'cm', type: 'number' },
      { key: 'uom', label: 'Units', help: 'e.g. gms/cm' },
    ],
  },
  {
    key: 'commercial',
    title: 'Pricing and codes',
    fields: [
      { key: 'mrp', label: 'MRP', unit: '₹', type: 'number' },
      { key: 'taxPercent', label: 'Tax', unit: '%', type: 'number', help: 'GST rate for this product' },
      { key: 'hsnCode', label: 'HSN code', mono: true },
      { key: 'eanCode', label: 'EAN code', mono: true },
      { key: 'upcCode', label: 'UPC code', mono: true },
      { key: 'productStatus', label: 'Status', select: ['ACTIVE', 'INACTIVE'] },
    ],
  },
];

export const PRODUCT_FIELDS = PRODUCT_FIELD_GROUPS.flatMap((g) => g.fields);

/** A blank product carrying every column the sheet expects, so nothing is undefined on save. */
export const emptyProduct = () =>
  PM_SHEET_HEADERS.reduce((acc, key) => ({ ...acc, [key]: '' }), { productStatus: 'ACTIVE' });

/** Status pill colouring, matching the admin module's vocabulary. */
export const statusTone = (status) => {
  const v = String(status || '').toUpperCase();
  if (['ACTIVE', 'ENABLED', 'LIVE'].includes(v)) return 'ACTIVE';
  if (['INACTIVE', 'DISABLED', 'BLOCKED', 'ARCHIVED'].includes(v)) return 'INACTIVE';
  return 'UNKNOWN';
};
